import { defaultTo, omit } from 'ramda'
import type {
  ChartDataRow,
  DimensionString,
  DimensionDate,
  DimensionStringOrDate,
  MetricAggregation,
  Dimension,
  DataKeyMap,
  MetricFilterType,
  ChartFilterValues,
  DimensionValuesSortType,
  ChartChoiceOptions,
  ChartCardChartDefinition,
  MetricsVariants,
  DateRangeChoice,
  TableColumnSortType,
  ChartCardDataProps
} from '../types'
import { isChartCardClientDataProps } from '../types'
import type {
  ChartCardDataPropsReady,
  ChartChoice,
  ChartChoicesMap,
  ChartDimensionAxisChoice,
  ChartDimensionValuesChoice,
  ChartDimensionValuesOfChoice,
  ChartVariantValueChoice,
  ChartVariantValuesChoice,
  ChoiceFilter,
  DimensionsMap,
  DimensionsValuesMap,
  MetricChoice,
  MetricsMap,
  MetricsVariantsChoice,
  TransposeMetric
} from '../internalTypes'
import { addDays, formatDateToString, differenceInDays } from 'shared/dateFns'
import { NULL_DIMENSION_VALUE_OPTION } from '../constants'
import {
  assertNever,
  errorLocal,
  generateIdForSelectionOthers,
  isNullDimensionValue,
  toNullDimensionOptionValue,
  isSelectionOthersId,
  strictObjectEntries,
  transposedMetricColumn,
  objectEntries,
  defaultToZero,
  evaluateFormula,
  setDifference,
  arrayToSorted,
  strictObjectValues,
  getLabelOrValueAsLabel
} from './generics'
import { getColumnDataKey } from '../components/table/utils/columnKeys'
import type { ChartCardTableColumnDef } from '../components/table/tanstack'

/**
 * Get the string dimension value from the row.
 *
 * If a formatting function is defined in the {@link DimensionString<'props', DataKey, DataRow>} type,
 * it is used to format the value.
 *
 * If a default value is defined in the {@link DimensionString<'props', DataKey, DataRow>} type,
 * it is used if the value is null or undefined.
 *
 * if the final value is an empty string, null is returned.
 *
 * @param row - The row.
 * @param dimension - The string dimension definition.
 * @returns The string dimension value.
 */
const getStringDimensionValue = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  row: DataRow,
  dimension: DimensionString<'choice', DataKey, DataRow> | MetricsVariants<'choice', DataKey, DataRow>,
  valuesToKeep: Set<string> | undefined
): string | null => {
  const { id, defaultValue, format } = dimension

  const rawValue =
    typeof format === 'function' ? format(id, row[id]) ?? defaultValue : (row[id] as string) ?? defaultValue
  const dimensionValue = rawValue === '' ? null : rawValue ?? null

  if (valuesToKeep == null) {
    return dimensionValue
  }

  if (dimensionValue == null) {
    // Null / empty rows are represented as NULL_DIMENSION_VALUE_OPTION in selections.
    if (valuesToKeep.has(NULL_DIMENSION_VALUE_OPTION)) {
      return NULL_DIMENSION_VALUE_OPTION
    }
    return generateIdForSelectionOthers(id)
  }

  if (valuesToKeep.has(dimensionValue)) {
    return dimensionValue
  }

  return generateIdForSelectionOthers(id)
}

/**
 * Normalises a string dimension cell for selection/filter membership checks.
 * Missing values (null / undefined / '') share {@link NULL_DIMENSION_VALUE_OPTION}.
 */
const toFilterableStringDimensionValue = toNullDimensionOptionValue

/**
 * Get the date dimension value as a string from the row.
 *
 * If a formatting function is defined in the {@link DimensionDate<DataKey>} type,
 * it is used to format the value.
 *
 * If a format string is defined in the {@link DimensionDate<DataKey>} type,
 * it is used to format the value.
 *
 * If a format object is defined in the {@link DimensionDate<DataKey>} type,
 * it is used to format the value.
 *
 * if no format is defined, the default format (yyyy-MM-dd) is used.
 *
 * @param row - The row.
 * @param dimension - The date dimension definition {@link DimensionDate<DataKey>}.
 * @returns The date as a string.
 */
const getDateDimensionValue = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  row: DataRow,
  dimension: DimensionDate<DataKey>,
  useFormat: boolean
): string | null => {
  const { id, format } = dimension
  const value = row[dimension.id]
  if (value == null || value === '' || typeof value === 'boolean') return null
  if (useFormat) {
    if (typeof format === 'function') {
      return format(id, value) ?? null
    }
    if (typeof format === 'object') {
      return formatDateToString({ date: value, ...format })
    }
    return formatDateToString({ date: value, targetFormat: format ?? 'yyyy-MM-dd' })
  }
  return formatDateToString({ date: value, targetFormat: 'yyyy-MM-dd' })
}

/**
 * Get the data dimension value from the row.
 *
 * If the dimension is a string dimension, the {@link getStringDimensionValue} function is used.
 *
 * If the dimension is a date dimension, the {@link getDateDimensionValue} function is used.
 *
 * @param row - The row.
 * @param dimension - The dimension definition {@link DimensionStringOrDate<'props', DataKey, DataRow>}.
 * @param valuesToKeep - The values to keep.
 * @returns The dimension value.
 */
const getDataDimensionValue = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  row: DataRow,
  dimension: Dimension<'choice', DataKey, DataRow>,
  valuesToKeep: Set<string> | undefined
): string | null => {
  switch (dimension.type) {
    case 'string':
    case 'variant':
      return getStringDimensionValue(row, dimension, valuesToKeep)
    case 'date':
      return getDateDimensionValue(row, dimension, false)
  }
}

/**
 * Replace missing dimension cells with {@link NULL_DIMENSION_VALUE_OPTION} so charts can draw them.
 *
 * Recharts cannot represent a missing category: it drops nil values from a category axis domain
 * (the bar loses its tick) and names a nil polar slice after the metric key. Chart data therefore
 * carries the canonical missing value, which every label path renders as
 * {@link NULL_DIMENSION_VALUE_DISPLAY} with a "No data" tooltip.
 *
 * Only dimensions whose collected values contain the canonical missing value are normalised, so
 * this is a no-op when null dimension values are disabled (see {@link getDimensionValues}).
 * Tables are left untouched — their cells keep null and are rendered by the table itself.
 *
 * @param data - The processed data.
 * @param dimensionsValuesMap - The collected dimension values.
 * @returns The data, or the same reference when there is nothing to normalise.
 */
export const normaliseNullDimensionValuesForChart = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  data,
  dimensionsValuesMap
}: {
  data: DataRow[]
  dimensionsValuesMap: Partial<DimensionsValuesMap<DataKey>> | undefined
}): DataRow[] => {
  if (dimensionsValuesMap == null) {
    return data
  }

  const nullableDimensions = (Object.keys(dimensionsValuesMap) as DataKey[]).filter(
    (id) => dimensionsValuesMap[id]?.has(NULL_DIMENSION_VALUE_OPTION) === true
  )
  if (nullableDimensions.length === 0) {
    return data
  }

  let normalised = false
  const normalisedData = data.map<DataRow>((row) => {
    let normalisedRow: DataRow | undefined
    for (const id of nullableDimensions) {
      if (row[id] !== NULL_DIMENSION_VALUE_OPTION && isNullDimensionValue(row[id])) {
        normalisedRow ??= { ...row }
        normalisedRow[id] = NULL_DIMENSION_VALUE_OPTION as DataRow[DataKey]
      }
    }
    if (normalisedRow == null) {
      return row
    }
    normalised = true
    return normalisedRow
  })

  return normalised ? normalisedData : data
}

/**
 * Collects dimension values, keeping {@link NULL_DIMENSION_VALUE_OPTION} only when null values are enabled.
 */
const toDimensionValuesSet = (values: string[], enableNullDimensionValue: boolean): Set<string> =>
  new Set<string>(enableNullDimensionValue ? values : values.filter((value) => value !== NULL_DIMENSION_VALUE_OPTION))

/**
 * Get the dimension values as a set of strings from the data.
 *
 * When `enableNullDimensionValue` is true, null / empty string dimension cells are collected as
 * {@link NULL_DIMENSION_VALUE_OPTION} so polar charts, chips, and multi-selects can keep a null
 * slice selected by default. When false they are dropped, as before, and rows carrying them fall
 * back to the "others" bucket.
 *
 * If the dimension is a string dimension {@link DimensionString<'props', DataKey, DataRow>}, the values are collected from the data.
 *
 * If the dimension is a date dimension {@link DimensionDate<DataKey>}, the values are formatted from the data.
 *
 * if the dimension is a variant dimension {@link MetricsVariants<'props', DataKey, DataRow>}, all the variant values are used.
 *
 * @param data - The data.
 * @param dimension - The dimension definition {@link Dimension<'props', DataKey, DataRow>}.
 * @param enableNullDimensionValue - Whether missing values become a selectable option, defaults to true.
 * @returns The dimension values as a set of strings.
 */
export const getDimensionValues = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  data: DataRow[],
  dimension: Dimension<'props', DataKey, DataRow>,
  enableNullDimensionValue = true
): Set<string> => {
  const { id, type, format } = dimension

  switch (type) {
    case 'string': {
      const defaultValue = dimension.defaultValue ?? NULL_DIMENSION_VALUE_OPTION

      const values =
        typeof format === 'function'
          ? data.map((row) => toNullDimensionOptionValue(format(id, row[id]) ?? defaultValue))
          : data.map((row) => toNullDimensionOptionValue((row[id] as string) ?? defaultValue))
      return toDimensionValuesSet(values, enableNullDimensionValue)
    }
    case 'date': {
      if (typeof format === 'function') {
        return toDimensionValuesSet(
          data.map((row) => toNullDimensionOptionValue(format(id, row[id]))),
          enableNullDimensionValue
        )
      }
      if (typeof format === 'string') {
        return toDimensionValuesSet(
          data.map((row) => {
            const date = row[id]
            if (typeof date === 'string' || typeof date === 'number' || date instanceof Date) {
              return formatDateToString({ date, targetFormat: format })
            }
            return NULL_DIMENSION_VALUE_OPTION
          }),
          enableNullDimensionValue
        )
      }
      if (typeof format === 'object') {
        return toDimensionValuesSet(
          data.map((row) =>
            row[id] == null || row[id] === ''
              ? NULL_DIMENSION_VALUE_OPTION
              : formatDateToString({ date: row[id] as Date | string | number, ...format })
          ),
          enableNullDimensionValue
        )
      }
      return toDimensionValuesSet(
        data.map((row) => (row[id] == null || row[id] === '' ? NULL_DIMENSION_VALUE_OPTION : (row[id] as string))),
        enableNullDimensionValue
      )
    }
    case 'variant':
      return new Set<string>(Object.keys(dimension.variantsMap))
    default:
      return assertNever(type)
  }
}

/**
 * Aggregate a metric value from a list of rows.
 *
 * The aggregation is done based on the {@link MetricAggregation} type.
 *
 * if no value can be found, the metric missingValue property (if any) is used.
 *
 * @param rows - The rows.
 * @param metric - The metric definition {@link Metric<DataKey>}.
 * @returns The aggregated metric value.
 */
export const aggregateMetric = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  rows: DataRow[],
  { id, type, aggregation: aggregationProp, missingValue = 0 }: MetricChoice<DataKey, DataRow>,
  overrideAggregation?: MetricAggregation
): number | null => {
  const aggregation = overrideAggregation ?? aggregationProp ?? (type === 'percentage' ? 'average' : 'sum')
  const reducerMap: Record<MetricAggregation, (acc: number | null, row: DataRow) => number | null> = {
    average: (acc, row) => {
      const value = defaultTo<number | null, number | null>(missingValue, row[id] as number | null)
      if (typeof value === 'number') {
        return (acc ?? 0) + value / rows.length
      }
      return acc
    },
    sum: (acc, row) => {
      const value = defaultTo<number | null, number | null>(missingValue, row[id] as number | null)
      if (typeof value === 'number') {
        return (acc ?? 0) + value
      }
      return acc
    },
    count: (acc, row) => {
      const value = defaultTo<number | null, number | null>(missingValue, row[id] as number | null)
      if (typeof value === 'number') {
        return (acc ?? 0) + 1
      }
      return acc
    },
    min: (acc, row) => {
      const value = defaultTo<number | null, number | null>(missingValue, row[id] as number | null)
      if (typeof value === 'number') {
        return Math.min(acc ?? Infinity, value)
      }
      return acc
    },
    max: (acc, row) => {
      const value = defaultTo<number | null, number | null>(missingValue, row[id] as number | null)
      if (typeof value === 'number') {
        return Math.max(acc ?? -Infinity, value)
      }
      return acc
    },
    any: (_, row) => defaultTo<number | null, number | null>(missingValue, row[id] as number | null)
  }

  const aggregated = rows.reduce<number | null>((acc, row) => reducerMap[aggregation](acc, row), null)

  return aggregated === Infinity || aggregated === -Infinity ? missingValue : aggregated
}

/**
 * Aggregate a metric, recalculating formula metrics from aggregated dependencies when
 * `recalculate` is true (ratio-of-sums), instead of averaging/summing precomputed row ratios.
 *
 * @param rows - The rows to aggregate.
 * @param metric - The metric definition.
 * @param metricsMap - Map used to resolve formula dependency metrics for aggregation.
 * @returns The aggregated (or recalculated) metric value.
 */
export const aggregateMetricRespectingRecalculate = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  rows: DataRow[],
  metric: MetricChoice<DataKey, DataRow>,
  metricsMap: MetricsMap<DataKey, DataRow>
): number | null => {
  const { formula, formulaMetrics, recalculate } = metric

  if (typeof formula === 'object' && formula.evaluate != null && formulaMetrics != null && recalculate === true) {
    const scope = formulaMetrics.reduce<Record<string, number>>((acc, formulaMetricId) => {
      const dependency = metricsMap[formulaMetricId] as MetricChoice<DataKey, DataRow> | undefined
      if (dependency == null) {
        acc[formulaMetricId] = 0
        return acc
      }

      const dependencyHasRecalculatedFormula =
        dependency.recalculate === true && typeof dependency.formula === 'object' && dependency.formula.evaluate != null

      acc[formulaMetricId] = dependencyHasRecalculatedFormula
        ? aggregateMetricRespectingRecalculate(rows, dependency, metricsMap) ?? 0
        : aggregateMetric(rows, dependency) ?? 0
      return acc
    }, {})

    return evaluateFormula(formula, scope)
  }

  return aggregateMetric(rows, metric)
}

/**
 * Whether a metric has no value a chart can plot above zero.
 *
 * Rows are shared across all metrics of a card, so a metric without data is absent or null on rows
 * that exist for other metrics, and date range placeholder rows carry no metric values at all.
 * Those rows count as zero here, the same way {@link aggregateMetric} treats them for the headline.
 *
 * @param rows - The rows to inspect.
 * @param dataKey - The metric data key.
 * @returns True when every row is zero, missing or non-finite, false for an empty row list.
 */
export const isMetricSeriesAllZero = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  rows: DataRow[],
  dataKey: DataKey
): boolean =>
  rows.length > 0 &&
  !rows.some((row) => {
    const value = row[dataKey]
    return typeof value === 'number' && Number.isFinite(value) && value !== 0
  })

/**
 * Set a metric to zero on every row, leaving the other keys untouched.
 *
 * Recharts skips null, undefined and non-finite points, so an all-zero series needs explicit zeros
 * to have anything to draw.
 *
 * @param rows - The rows to copy.
 * @param dataKey - The metric data key to zero.
 * @returns New rows with the metric set to zero.
 */
export const zeroFillMetricValues = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  rows: DataRow[],
  dataKey: DataKey
): DataRow[] =>
  rows.map((row) => {
    const zeroFilledRow: DataRow = { ...row }
    zeroFilledRow[dataKey] = 0 as DataRow[DataKey]
    return zeroFilledRow
  })

const createTransposeFilters = <DataKey extends string>(
  transposeMetrics: Array<TransposeMetric<DataKey>>
): Array<ChoiceFilter<DataKey>> => {
  const filteredDimension = new Set<DataKey>()
  const filters = transposeMetrics
    .filter(({ filterByValue, value, values }) => filterByValue && (value != null || values.length > 0))
    .map<ChoiceFilter<DataKey>>(({ type, dimension, value, values }) =>
      type === 'multiple'
        ? {
            id: dimension,
            value: values.map(({ value }) => value),
            clause: 'in'
          }
        : {
            id: dimension,
            value: value.value,
            clause: '='
          }
    )
    .filter(({ id }) => {
      if (filteredDimension.has(id)) {
        return false
      }
      filteredDimension.add(id)
      return true
    })

  return filters
}

const transposeGroupedRows = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  withTranspose,
  metrics,
  columnsToKeep,
  newMetrics,
  transposeMetrics,
  metricsMap,
  newMetricsMap,
  transposedMetricsLists,
  groupedRows
}: {
  withTranspose: boolean
  metrics: DataKey[]
  columnsToKeep: DataKey[]
  newMetrics: Set<DataKey>
  transposeMetrics: Array<TransposeMetric<DataKey>>
  metricsMap: MetricsMap<DataKey, DataRow>
  newMetricsMap: MetricsMap<DataKey, DataRow>
  transposedMetricsLists: Map<DataKey, Set<DataKey>>
  groupedRows: DataRow[]
}): { rows: DataRow[]; newGroupMetrics: DataKey[]; needsAggregation: boolean } => {
  if (!withTranspose) {
    return { rows: groupedRows, newGroupMetrics: metrics, needsAggregation: false }
  }

  const newGroupMetrics: DataKey[] = []

  const isSingle: DataKeyMap<DataKey, boolean> = transposeMetrics.reduce<DataKeyMap<DataKey, boolean>>(
    (acc, { dimension, type }) => {
      acc[dimension] = type === 'single'
      return acc
    },
    {}
  )

  let needsAggregation = false

  const transposeMetricsMap = transposeMetrics.reduce<DataKeyMap<DataKey, DataKey>>(
    (acc, { type, choiceId, metric, dimension, value, values }) => {
      let newMetricMetrics = transposedMetricsLists.get(metric)
      if (newMetricMetrics == null) {
        newMetricMetrics = new Set<DataKey>()
        transposedMetricsLists.set(choiceId, newMetricMetrics)
        transposedMetricsLists.set(metric, newMetricMetrics)
      }
      const dimensionValue = groupedRows[0][dimension] as string
      needsAggregation ||= isSelectionOthersId(dimensionValue)
      if (!isSelectionOthersId(dimensionValue) || isSingle[dimension] !== true) {
        const newMetric = transposedMetricColumn(metric, dimensionValue)
        newGroupMetrics.push(newMetric)
        if (!newMetricMetrics.has(newMetric)) {
          newMetricMetrics.add(newMetric)
          const valueDefinition = value ?? values.find(({ value }) => value === dimensionValue)
          newMetrics.add(newMetric)
          if (!(metric in metricsMap)) {
            errorLocal(`transposeData ${type} ${metric} not in metricsMap`, {
              metric,
              dimensionValue,
              transposeMetrics
            })
          }
          const transposedMetric = metricsMap[metric] as MetricChoice<DataKey, DataRow>
          newMetricsMap[newMetric] = {
            ...transposedMetric,
            id: newMetric,
            ...valueDefinition,
            label: `${transposedMetric.label ?? metric} (${getLabelOrValueAsLabel(
              valueDefinition?.label,
              dimensionValue
            )})`,
            description: transposedMetric.description
          }
        }
        acc[metric] = newMetric
      }
      return acc
    },
    {}
  )

  const mappedRows = groupedRows.map<DataRow>(
    (row) =>
      columnsToKeep.reduce<Partial<DataRow>>((acc, column) => {
        if (column in transposeMetricsMap) {
          acc[transposeMetricsMap[column] as DataKey] = row[column]
        } else {
          acc[column] = row[column]
        }
        return acc
      }, {}) as DataRow
  )

  newGroupMetrics.push(...metrics.filter((metric) => !transposedMetricsLists.has(metric)))

  return { rows: mappedRows, newGroupMetrics, needsAggregation }
}

type GroupDataResult<DataKey extends string, DataRow extends ChartDataRow<DataKey>> =
  | { groupedData: Map<string, DataRow[]>; needsAggregation: true }
  | { groupedData?: never; needsAggregation: false }

const groupData = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  data,
  useGroupBy,
  metrics,
  dimensionsMap,
  transposeValuesToKeepMap,
  dataProcessed
}: {
  data: DataRow[] | undefined
  useGroupBy: DataKey[]
  metrics: DataKey[]
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  transposeValuesToKeepMap: DataKeyMap<DataKey, Set<string>>
  dataProcessed: boolean
}): GroupDataResult<DataKey, DataRow> => {
  if (data == null) {
    return { needsAggregation: false }
  }

  const groupedData = data.reduce<Map<string, DataRow[]>>((groupedMap, row) => {
    const groupByRow = useGroupBy.reduce<Partial<DataRow>>((cleaned, id) => {
      cleaned[id] = dataProcessed
        ? row[id]
        : (getDataDimensionValue(
            row,
            dimensionsMap[id] as DimensionStringOrDate<'choice', DataKey, DataRow>,
            transposeValuesToKeepMap[id]
          ) as DataRow[DataKey])
      return cleaned
    }, {}) as DataRow
    const groupByValue = useGroupBy.map((id) => groupByRow[id]).join('\r')
    const cleanedRow = metrics.reduce<Partial<DataRow>>((cleaned, id) => {
      cleaned[id] = row[id]
      return cleaned
    }, groupByRow) as DataRow
    const rows = groupedMap.get(groupByValue)
    if (rows == null) {
      groupedMap.set(groupByValue, [cleanedRow])
    } else {
      rows.push(cleanedRow)
    }
    return groupedMap
  }, new Map<string, DataRow[]>())

  if (groupedData.size !== data.length) {
    return { groupedData, needsAggregation: true }
  }

  return { needsAggregation: false }
}

const transposeRow = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  row,
  withTranspose,
  columnsToKeep,
  newMetrics,
  transposeMetrics,
  metricsMap,
  newMetricsMap,
  transposedMetricsLists,
  metrics,
  transposedRows
}: {
  row: DataRow
  withTranspose: boolean
  columnsToKeep: DataKey[]
  newMetrics: Set<DataKey>
  transposeMetrics: Array<TransposeMetric<DataKey>>
  metricsMap: MetricsMap<DataKey, DataRow>
  newMetricsMap: MetricsMap<DataKey, DataRow>
  transposedMetricsLists: Map<DataKey, Set<DataKey>>
  metrics: DataKey[]
  transposedRows: DataRow[]
}): void => {
  if (withTranspose) {
    const { rows } = transposeGroupedRows<DataKey, DataRow>({
      withTranspose,
      metrics,
      columnsToKeep,
      newMetrics,
      transposeMetrics,
      metricsMap,
      newMetricsMap,
      transposedMetricsLists,
      groupedRows: [row]
    })

    transposedRows.push(rows[0])
  } else {
    transposedRows.push(row)
  }
}

const aggregateAndTransposeGroupedRows = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  groupedRows,
  withTranspose,
  columnsToKeep,
  newMetrics,
  transposeMetrics,
  metricsMap,
  newMetricsMap,
  transposedMetricsLists,
  metrics,
  aggregatedRows,
  dataProcessed
}: {
  groupedRows: DataRow[]
  withTranspose: boolean
  columnsToKeep: DataKey[]
  newMetrics: Set<DataKey>
  transposeMetrics: Array<TransposeMetric<DataKey>>
  metricsMap: MetricsMap<DataKey, DataRow>
  newMetricsMap: MetricsMap<DataKey, DataRow>
  transposedMetricsLists: Map<DataKey, Set<DataKey>>
  metrics: DataKey[]
  aggregatedRows: DataRow[]
  dataProcessed: boolean
}): void => {
  const { rows, newGroupMetrics } = transposeGroupedRows<DataKey, DataRow>({
    withTranspose,
    columnsToKeep,
    metrics,
    newMetrics,
    transposeMetrics,
    metricsMap,
    newMetricsMap,
    transposedMetricsLists,
    groupedRows
  })

  const baseRow: DataRow = { ...rows[0] }

  const metricsToAggregate = newGroupMetrics.map(
    (metric) => (newMetricsMap[metric] ?? metricsMap[metric]) as MetricChoice<DataKey, DataRow>
  )

  if (rows.length > 1) {
    for (const metric of metricsToAggregate) {
      if (dataProcessed || metric.formula == null) {
        baseRow[metric.id] = (aggregateMetric(rows, metric) ?? 0) as DataRow[DataKey]
      }
    }
  }

  if (!dataProcessed) {
    for (const metric of metricsToAggregate) {
      calculateMetric(baseRow, metric)
    }
  }

  aggregatedRows.push(baseRow)
}

const calculateMetric = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  row: DataRow,
  metric: MetricChoice<DataKey, DataRow>
): void => {
  const { id, formula, formulaMetrics } = metric

  if (formula == null) {
    return
  }

  if (typeof formula === 'function') {
    row[id] = formula(id, row) as DataRow[DataKey]
    return
  }

  const scope =
    formulaMetrics?.reduce<Record<string, number>>((scope, metric) => {
      scope[metric] = defaultTo<number, number>(0, row[metric] as number | null)
      return scope
    }, {}) ?? {}

  row[id] = evaluateFormula(formula, scope) as DataRow[DataKey]
}

const aggregateAndTransposeRowsGroups = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  groupedData,
  withTranspose,
  columnsToKeep,
  newMetrics,
  transposeMetrics,
  metricsMap,
  newMetricsMap,
  transposedMetricsLists,
  metrics,
  aggregatedAndTransposedData,
  dataProcessed
}: {
  groupedData: Map<string, DataRow[]>
  withTranspose: boolean
  columnsToKeep: DataKey[]
  newMetrics: Set<DataKey>
  transposeMetrics: Array<TransposeMetric<DataKey>>
  metricsMap: MetricsMap<DataKey, DataRow>
  newMetricsMap: MetricsMap<DataKey, DataRow>
  transposedMetricsLists: Map<DataKey, Set<DataKey>>
  metrics: DataKey[]
  aggregatedAndTransposedData: DataRow[]
  dataProcessed: boolean
}): void => {
  for (const groupedRows of groupedData.values()) {
    aggregateAndTransposeGroupedRows({
      groupedRows,
      withTranspose,
      columnsToKeep,
      newMetrics,
      transposeMetrics,
      metricsMap,
      newMetricsMap,
      transposedMetricsLists,
      metrics,
      aggregatedRows: aggregatedAndTransposedData,
      dataProcessed
    })
  }
}

const transposeRows = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  filteredData,
  withTranspose,
  columnsToKeep,
  newMetrics,
  transposeMetrics,
  metricsMap,
  newMetricsMap,
  transposedMetricsLists,
  metrics,
  aggregatedAndTransposedData
}: {
  filteredData: DataRow[]
  withTranspose: boolean
  columnsToKeep: DataKey[]
  newMetrics: Set<DataKey>
  transposeMetrics: Array<TransposeMetric<DataKey>>
  metricsMap: MetricsMap<DataKey, DataRow>
  newMetricsMap: MetricsMap<DataKey, DataRow>
  transposedMetricsLists: Map<DataKey, Set<DataKey>>
  metrics: DataKey[]
  aggregatedAndTransposedData: DataRow[]
}): void => {
  for (const row of filteredData) {
    transposeRow({
      row,
      withTranspose,
      columnsToKeep,
      newMetrics,
      transposeMetrics,
      metricsMap,
      newMetricsMap,
      transposedMetricsLists,
      metrics,
      transposedRows: aggregatedAndTransposedData
    })
  }
}

export function processDataForCalculatedMetrics<DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  data,
  metricsMap,
  inPlace
}: {
  data: DataRow[]
  metricsMap: MetricsMap<DataKey, DataRow>
  inPlace?: true
}): DataRow[] {
  const calculatedMetrics = strictObjectValues(metricsMap).filter((metric) => metric.formula != null)

  if (calculatedMetrics.length === 0) {
    return data
  }

  if (inPlace === true) {
    for (const row of data) {
      for (const metric of calculatedMetrics) {
        calculateMetric(row, metric)
      }
    }
    return data
  }

  return data.map((row) => {
    const calculatedRow = { ...row }
    for (const metric of calculatedMetrics) {
      calculateMetric(calculatedRow, metric)
    }
    return calculatedRow
  })
}

const getTransposeValuesToKeepMap = <DataKey extends string>(
  transposeMetrics: Array<TransposeMetric<DataKey>>
): DataKeyMap<DataKey, Set<string>> =>
  transposeMetrics.reduce<DataKeyMap<DataKey, Set<string>>>((acc, { dimension, value, values }) => {
    if (acc[dimension] != null) {
      const set = acc[dimension]
      if (value == null) {
        for (const { value } of values) {
          set.add(value)
        }
      } else {
        set.add(value.value)
      }
      return acc
    }
    if (value == null) {
      acc[dimension] = new Set(values.map(({ value }) => value))
    } else {
      acc[dimension] = new Set([value.value])
    }
    return acc
  }, {})

const aggregateAndTransposeRows = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  filteredData,
  filteredDataCompare,
  groupDataResult,
  groupDataCompareResult,
  groupBy,
  withTranspose,
  transposeMetrics,
  dimensionsMap,
  metricsMap,
  newMetricsMap,
  transposedMetricsLists,
  metrics,
  dataProcessed
}: {
  filteredData: DataRow[]
  filteredDataCompare: DataRow[] | undefined
  groupDataResult: GroupDataResult<DataKey, DataRow>
  groupDataCompareResult: GroupDataResult<DataKey, DataRow>
  groupBy: DataKey[]
  withTranspose: boolean
  transposeMetrics: Array<TransposeMetric<DataKey>>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  newMetricsMap: MetricsMap<DataKey, DataRow>
  transposedMetricsLists: Map<DataKey, Set<DataKey>>
  metrics: DataKey[]
  dataProcessed: boolean
}): {
  aggregatedAndTransposedData: DataRow[]
  aggregatedAndTransposedDataCompare: DataRow[] | undefined
  newMetricsMap: MetricsMap<DataKey, DataRow>
} => {
  const withCompare = filteredDataCompare != null
  const { groupedData, needsAggregation } = groupDataResult
  const { groupedData: groupedDataCompare, needsAggregation: needsAggregationCompare } = groupDataCompareResult

  let aggregatedAndTransposedData = filteredData
  let aggregatedAndTransposedDataCompare = filteredDataCompare
  const newMetrics = new Set<DataKey>()
  const newMetricsCompare = new Set<DataKey>()
  const columnsToKeep = [...groupBy, ...metrics]

  if (needsAggregation) {
    aggregatedAndTransposedData = []
    aggregateAndTransposeRowsGroups<DataKey, DataRow>({
      groupedData,
      withTranspose,
      columnsToKeep,
      newMetrics,
      transposeMetrics,
      metricsMap,
      newMetricsMap,
      transposedMetricsLists,
      metrics,
      aggregatedAndTransposedData,
      dataProcessed
    })
  } else if (withTranspose) {
    aggregatedAndTransposedData = []
    transposeRows({
      filteredData,
      withTranspose,
      columnsToKeep,
      newMetrics,
      transposeMetrics,
      metricsMap,
      newMetricsMap,
      transposedMetricsLists,
      metrics,
      aggregatedAndTransposedData
    })
    processDataForCalculatedMetrics({ data: aggregatedAndTransposedData, metricsMap, inPlace: true })
  } else if (!dataProcessed) {
    aggregatedAndTransposedData = processDataForCalculatedMetrics({ data: filteredData, metricsMap })
  }
  if (needsAggregationCompare) {
    aggregatedAndTransposedDataCompare = []
    aggregateAndTransposeRowsGroups<DataKey, DataRow>({
      groupedData: groupedDataCompare,
      withTranspose,
      columnsToKeep,
      newMetrics: newMetricsCompare,
      transposeMetrics,
      metricsMap,
      newMetricsMap,
      transposedMetricsLists,
      metrics,
      aggregatedAndTransposedData: aggregatedAndTransposedDataCompare,
      dataProcessed
    })
  } else if (withTranspose && withCompare) {
    aggregatedAndTransposedDataCompare = []
    transposeRows({
      filteredData: filteredDataCompare,
      withTranspose,
      columnsToKeep,
      newMetrics: newMetricsCompare,
      transposeMetrics,
      metricsMap,
      newMetricsMap,
      transposedMetricsLists,
      metrics,
      aggregatedAndTransposedData: aggregatedAndTransposedDataCompare
    })
    processDataForCalculatedMetrics({ data: aggregatedAndTransposedDataCompare, metricsMap, inPlace: true })
  } else if (!dataProcessed && withCompare) {
    aggregatedAndTransposedDataCompare = processDataForCalculatedMetrics({ data: filteredDataCompare, metricsMap })
  }

  if (withTranspose) {
    for (const metric of metrics) {
      if (!transposedMetricsLists.has(metric)) {
        newMetrics.add(metric)
      }
    }

    return {
      ...aggregateAndTransposeData<DataKey, DataRow>({
        data: aggregatedAndTransposedData,
        dataCompare: aggregatedAndTransposedDataCompare,
        groupBy,
        metrics: Array.from(newMetrics),
        metricsMap: newMetricsMap,
        dimensionsMap,
        transposedMetricsLists,
        transposeMetrics: [],
        dataProcessed: true
      }),
      newMetricsMap
    }
  }

  return {
    aggregatedAndTransposedData,
    aggregatedAndTransposedDataCompare,
    newMetricsMap
  }
}

export function aggregateAndTransposeData<DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  data,
  dataCompare,
  groupBy,
  metrics,
  metricsMap,
  dimensionsMap,
  transposedMetricsLists,
  transposeMetrics,
  dataProcessed
}: {
  data: DataRow[]
  dataCompare: undefined
  groupBy: DataKey[]
  metrics: DataKey[]
  metricsMap: MetricsMap<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  transposedMetricsLists: Map<DataKey, Set<DataKey>>
  transposeMetrics: Array<TransposeMetric<DataKey>>
  dataProcessed?: true
}): {
  aggregatedAndTransposedData: DataRow[]
  aggregatedAndTransposedDataCompare: undefined
  newMetricsMap: MetricsMap<DataKey, DataRow>
}
export function aggregateAndTransposeData<DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  data,
  dataCompare,
  groupBy,
  metrics,
  metricsMap,
  dimensionsMap,
  transposedMetricsLists,
  transposeMetrics,
  dataProcessed
}: {
  data: DataRow[]
  dataCompare: DataRow[]
  groupBy: DataKey[]
  metrics: DataKey[]
  metricsMap: MetricsMap<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  transposedMetricsLists: Map<DataKey, Set<DataKey>>
  transposeMetrics: Array<TransposeMetric<DataKey>>
  dataProcessed?: true
}): {
  aggregatedAndTransposedData: DataRow[]
  aggregatedAndTransposedDataCompare: DataRow[]
  newMetricsMap: MetricsMap<DataKey, DataRow>
}
export function aggregateAndTransposeData<DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  data,
  dataCompare,
  groupBy,
  metrics,
  metricsMap,
  dimensionsMap,
  transposedMetricsLists,
  transposeMetrics,
  dataProcessed
}: {
  data: DataRow[]
  dataCompare: DataRow[] | undefined
  groupBy: DataKey[]
  metrics: DataKey[]
  metricsMap: MetricsMap<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  transposedMetricsLists: Map<DataKey, Set<DataKey>>
  transposeMetrics: Array<TransposeMetric<DataKey>>
  dataProcessed?: true
}): {
  aggregatedAndTransposedData: DataRow[]
  aggregatedAndTransposedDataCompare: DataRow[] | undefined
  newMetricsMap: MetricsMap<DataKey, DataRow>
}
export function aggregateAndTransposeData<DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  data,
  dataCompare,
  groupBy,
  metrics,
  metricsMap,
  dimensionsMap,
  transposedMetricsLists,
  transposeMetrics,
  dataProcessed
}: {
  data: DataRow[]
  dataCompare: DataRow[] | undefined
  groupBy: DataKey[]
  metrics: DataKey[]
  metricsMap: MetricsMap<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  transposedMetricsLists: Map<DataKey, Set<DataKey>>
  transposeMetrics: Array<TransposeMetric<DataKey>>
  dataProcessed?: true
}): {
  aggregatedAndTransposedData: DataRow[]
  aggregatedAndTransposedDataCompare: DataRow[] | undefined
  newMetricsMap: MetricsMap<DataKey, DataRow>
} {
  const filledData = fillData({ data, metrics })
  const filledDataCompare = dataCompare == null ? undefined : fillData({ data: dataCompare, metrics })

  const withTranspose = transposeMetrics.length > 0

  const newMetricsMap = withTranspose ? { ...metricsMap } : metricsMap

  const groupByWithTransposeDimensions = withTranspose
    ? groupBy.concat([...new Set(transposeMetrics.map(({ dimension }) => dimension))])
    : groupBy

  const transposeValuesToKeepMap = getTransposeValuesToKeepMap<DataKey>(transposeMetrics)

  const { filteredData, filteredDataCompare } = applyFilters<DataKey, DataRow>({
    data: filledData,
    dataCompare: filledDataCompare,
    dimensionsMap,
    filters: createTransposeFilters(transposeMetrics)
  })

  const groupDataResult = groupData<DataKey, DataRow>({
    data: filteredData,
    useGroupBy: groupByWithTransposeDimensions,
    metrics,
    dimensionsMap,
    transposeValuesToKeepMap,
    dataProcessed: dataProcessed === true
  })

  const groupDataCompareResult = groupData<DataKey, DataRow>({
    data: filteredDataCompare,
    useGroupBy: groupByWithTransposeDimensions,
    metrics,
    dimensionsMap,
    transposeValuesToKeepMap,
    dataProcessed: dataProcessed === true
  })

  return aggregateAndTransposeRows<DataKey, DataRow>({
    filteredData,
    filteredDataCompare,
    groupDataResult,
    groupDataCompareResult,
    groupBy,
    withTranspose,
    transposeMetrics,
    dimensionsMap,
    metricsMap,
    newMetricsMap,
    transposedMetricsLists,
    metrics,
    dataProcessed: dataProcessed === true
  })
}

const fillData = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  data,
  metrics
}: {
  data: DataRow[]
  metrics: DataKey[]
}): DataRow[] => {
  return data.map((row) =>
    metrics.reduce<DataRow>(
      (acc, metric) => {
        acc[metric] ??= 0 as DataRow[DataKey]
        return acc
      },
      { ...row }
    )
  )
}

const untransposeVariantRow = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  newDimension,
  valueMetrics,
  deleteMetrics,
  row,
  untransposedData
}: {
  newDimension: DataKey
  valueMetrics: Array<[string, Array<[DataKey, DataKey]>]>
  deleteMetrics: DataKey[]
  row: DataRow
  untransposedData: DataRow[]
}): void => {
  for (const [value, newMetrics] of valueMetrics) {
    untransposedData.push(
      newMetrics.reduce<DataRow>(
        (acc, [newMetric, oldMetric]) => {
          acc[newMetric] = row[oldMetric]
          return acc
        },
        {
          ...(omit(deleteMetrics, row) as DataRow),
          [newDimension]: value
        }
      )
    )
  }
}

function untransposeVariant<DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  data: DataRow[],
  dataCompare: DataRow[],
  variant: MetricsVariantsChoice<DataKey, DataRow>,
  metricsMap: MetricsMap<DataKey, DataRow>
): { untransposedData: DataRow[]; untransposedDataCompare: DataRow[]; newMetricsMap: MetricsMap<DataKey, DataRow> }
function untransposeVariant<DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  data: DataRow[],
  dataCompare: DataRow[] | undefined,
  variant: MetricsVariantsChoice<DataKey, DataRow>,
  metricsMap: MetricsMap<DataKey, DataRow>
): {
  untransposedData: DataRow[]
  untransposedDataCompare: DataRow[] | undefined
  newMetricsMap: MetricsMap<DataKey, DataRow>
}
function untransposeVariant<DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  data: DataRow[],
  dataCompare: DataRow[] | undefined,
  variant: MetricsVariantsChoice<DataKey, DataRow>,
  metricsMap: MetricsMap<DataKey, DataRow>
): {
  untransposedData: DataRow[]
  untransposedDataCompare: DataRow[] | undefined
  newMetricsMap: MetricsMap<DataKey, DataRow>
} {
  const untransposedData: DataRow[] = []
  const untransposedDataCompare: DataRow[] = []
  const { newMetrics, variantsMap } = variant
  const valueMetrics = objectEntries(variantsMap).map<[string, Array<[DataKey, DataKey]>]>(([value, metricsMap]) => [
    value,
    strictObjectEntries(metricsMap)
  ])
  const deleteMetrics: DataKey[] = valueMetrics.flatMap(([_, metrics]) => metrics.map(([_, metric]) => metric))
  const newMetricsMap = objectEntries(variantsMap).reduce<MetricsMap<DataKey, DataRow>>((acc, [_, variantMap]) => {
    for (const [newMetric, metric] of strictObjectEntries(variantMap)) {
      acc[newMetric] = {
        ...(omit(['variant'], metricsMap[metric]) as MetricChoice<DataKey, DataRow>),
        ...newMetrics[newMetric],
        id: newMetric
      }
    }
    return acc
  }, omit(deleteMetrics, metricsMap) as MetricsMap<DataKey, DataRow>)

  for (const row of data) {
    untransposeVariantRow({
      newDimension: variant.id,
      valueMetrics,
      deleteMetrics,
      row,
      untransposedData
    })
  }

  if (dataCompare == null) {
    return { untransposedData, untransposedDataCompare: undefined, newMetricsMap }
  }

  for (const row of dataCompare) {
    untransposeVariantRow({
      newDimension: variant.id,
      valueMetrics,
      deleteMetrics,
      row,
      untransposedData: untransposedDataCompare
    })
  }

  return { untransposedData, untransposedDataCompare, newMetricsMap }
}

const filterIsChoiceVariantValueOrValues = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  choice: ChartChoice<DataKey, DataRow>
): choice is ChartVariantValueChoice<DataKey, DataRow> | ChartVariantValuesChoice<DataKey, DataRow> =>
  choice.type === 'variantValue' || choice.type === 'variantValues'

const getChartOrTableVariantValueOrValuesChoice = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  chart,
  columns,
  choicesMap
}: {
  chart: ChartCardChartDefinition<DataKey, DataRow> | undefined
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>> | undefined
  choicesMap: ChartChoicesMap<DataKey, DataRow>
}): ChartVariantValueChoice<DataKey, DataRow> | ChartVariantValuesChoice<DataKey, DataRow> | undefined => {
  if (chart != null) {
    const { dimensionAxis, firstAxis, thirdAxis } = chart
    const axisKey = dimensionAxis === 'third' ? thirdAxis : firstAxis
    const choice = choicesMap[axisKey]
    if (choice == null) {
      return undefined
    }
    if (choice.type === 'variantValue' || choice.type === 'variantValues') {
      return choice
    }
    return undefined
  }

  if (columns != null) {
    const choice = columns
      .map((column) => {
        const dataKey = getColumnDataKey<DataKey, DataRow>(column)
        if (dataKey == null) {
          return undefined
        }
        return choicesMap[dataKey]
      })
      .filter((choice) => choice != null)
      .filter(filterIsChoiceVariantValueOrValues)
      .find((choice) => choice != null)
    return choice
  }

  return undefined
}

export function untransposeData<DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  data,
  dataCompare,
  chart,
  columns,
  choicesMap,
  metricsMap,
  dimensionsMap
}: {
  data: DataRow[]
  dataCompare: DataRow[]
  chart: ChartCardChartDefinition<DataKey, DataRow> | undefined
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>> | undefined
  choicesMap: ChartChoicesMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
}): { untransposedData: DataRow[]; untransposedDataCompare: DataRow[]; newMetricsMap: MetricsMap<DataKey, DataRow> }
export function untransposeData<DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  data,
  dataCompare,
  chart,
  columns,
  choicesMap,
  metricsMap,
  dimensionsMap
}: {
  data: DataRow[]
  dataCompare: DataRow[] | undefined
  chart: ChartCardChartDefinition<DataKey, DataRow> | undefined
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>> | undefined
  choicesMap: ChartChoicesMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
}): {
  untransposedData: DataRow[]
  untransposedDataCompare: DataRow[] | undefined
  newMetricsMap: MetricsMap<DataKey, DataRow>
}
export function untransposeData<DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  data,
  dataCompare,
  chart,
  columns,
  choicesMap,
  metricsMap,
  dimensionsMap
}: {
  data: DataRow[]
  dataCompare: DataRow[] | undefined
  chart: ChartCardChartDefinition<DataKey, DataRow> | undefined
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>> | undefined
  choicesMap: ChartChoicesMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
}): {
  untransposedData: DataRow[]
  untransposedDataCompare: DataRow[] | undefined
  newMetricsMap: MetricsMap<DataKey, DataRow>
} {
  const choice = getChartOrTableVariantValueOrValuesChoice<DataKey, DataRow>({ chart, columns, choicesMap })
  if (choice == null) {
    return { untransposedData: data, untransposedDataCompare: dataCompare, newMetricsMap: metricsMap }
  }

  const variant = dimensionsMap[choice.dimension]
  if (variant?.type !== 'variant') {
    errorLocal(
      `untransposeData ${choice.id} invalid variantValue(s) choice variant dimension type ${choice.dimension} ${
        variant?.type ?? 'undefined'
      }`,
      { choice, variant }
    )
    return { untransposedData: data, untransposedDataCompare: dataCompare, newMetricsMap: metricsMap }
  }

  return untransposeVariant<DataKey, DataRow>(data, dataCompare, variant, metricsMap)
}

export function applyFilters<DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  data,
  dataCompare,
  dimensionsMap,
  filters
}: {
  data: DataRow[]
  dataCompare: undefined
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  filters: Array<ChoiceFilter<DataKey>>
}): { filteredData: DataRow[]; filteredDataCompare: undefined }
export function applyFilters<DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  data,
  dataCompare,
  dimensionsMap,
  filters
}: {
  data: DataRow[]
  dataCompare: DataRow[]
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  filters: Array<ChoiceFilter<DataKey>>
}): { filteredData: DataRow[]; filteredDataCompare: DataRow[] }
export function applyFilters<DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  data,
  dataCompare,
  dimensionsMap,
  filters
}: {
  data: DataRow[]
  dataCompare: DataRow[] | undefined
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  filters: Array<ChoiceFilter<DataKey>>
}): { filteredData: DataRow[]; filteredDataCompare: DataRow[] | undefined }
export function applyFilters<DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  data,
  dataCompare,
  dimensionsMap,
  filters
}: {
  data: DataRow[]
  dataCompare: DataRow[] | undefined
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  filters: Array<ChoiceFilter<DataKey>>
}): { filteredData: DataRow[]; filteredDataCompare: DataRow[] | undefined } {
  if (filters.length === 0) {
    return { filteredData: data, filteredDataCompare: dataCompare }
  }

  let filteredData: DataRow[] = data
  let filteredDataCompare: DataRow[] | undefined = dataCompare

  for (const filter of filters) {
    const { id, clause, value } = filter
    const dimension = dimensionsMap[id]

    switch (clause) {
      case '=':
      case '!=': {
        filteredData = filteredData.filter((row) => {
          const rowValue =
            dimension?.type === 'date'
              ? getDateDimensionValue(row, dimension, true) ?? NULL_DIMENSION_VALUE_OPTION
              : toFilterableStringDimensionValue(row[id])
          return (value === rowValue) === (clause === '=')
        })
        filteredDataCompare = filteredDataCompare?.filter((row) => {
          const rowValue =
            dimension?.type === 'date'
              ? getDateDimensionValue(row, dimension, true) ?? NULL_DIMENSION_VALUE_OPTION
              : toFilterableStringDimensionValue(row[id])
          return (value === rowValue) === (clause === '=')
        })
        break
      }
      case 'in':
      case 'out': {
        if (value.length > 0) {
          filteredData = filteredData.filter((row) => {
            const rowValue =
              dimension?.type === 'date'
                ? getDateDimensionValue(row, dimension, true) ?? NULL_DIMENSION_VALUE_OPTION
                : toFilterableStringDimensionValue(row[id])
            return value.includes(rowValue) === (clause === 'in')
          })
          filteredDataCompare = filteredDataCompare?.filter((row) => {
            const rowValue =
              dimension?.type === 'date'
                ? getDateDimensionValue(row, dimension, true) ?? NULL_DIMENSION_VALUE_OPTION
                : toFilterableStringDimensionValue(row[id])
            return value.includes(rowValue) === (clause === 'in')
          })
        }
        break
      }
      default:
        assertNever(clause)
    }
  }

  return { filteredData, filteredDataCompare }
}

const sortMappedDataAlphabetically = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  mappedData: Record<string, DataRow[]>,
  sort: 'asc' | 'desc'
): DataRow[] => {
  switch (sort) {
    case 'asc':
      return Object.entries(mappedData)
        .sort(([valueA], [valueB]) => valueA.localeCompare(valueB))
        .flatMap(([_, rows]) => rows)
    case 'desc':
      return Object.entries(mappedData)
        .sort(([valueA], [valueB]) => valueB.localeCompare(valueA))
        .flatMap(([_, rows]) => rows)
    default:
      assertNever(sort)
      return Object.values(mappedData).flat()
  }
}

const sortMappedDataNumerically = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  mappedData: Record<number, DataRow[]>,
  sort: DimensionValuesSortType
): DataRow[] => {
  switch (sort) {
    case 'asc':
    case 'metricsAsc':
      return Object.entries(mappedData)
        .sort(([valueA], [valueB]) => Number(valueA) - Number(valueB))
        .flatMap(([_, rows]) => rows)

    case 'desc':
    case 'metricsDesc':
      return Object.entries(mappedData)
        .sort(([valueA], [valueB]) => Number(valueB) - Number(valueA))
        .flatMap(([_, rows]) => rows)
    default:
      assertNever(sort)
      return Object.values(mappedData).flat()
  }
}

const mapAggregatedData = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  aggregatedData: DataRow[],
  dimension: DataKey,
  options: ChartChoiceOptions<string>
): Record<string, DataRow[]> =>
  aggregatedData.reduce<Record<string, DataRow[]>>((acc, row) => {
    const dimensionValue = toNullDimensionOptionValue(row[dimension])
    const sortValue =
      options.find(({ value }) => value === dimensionValue)?.label ?? getLabelOrValueAsLabel(undefined, dimensionValue)
    acc[sortValue] = [...(acc[sortValue] ?? []), row]
    return acc
  }, {})

const mapAggregatedDataForDate = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  aggregatedData: DataRow[],
  dimension: DataKey
): Record<string, DataRow[]> =>
  aggregatedData.reduce<Record<string, DataRow[]>>((acc, row) => {
    const date = row[dimension] as string | null | undefined
    const sortValue = date == null ? '' : formatDateToString({ date, targetFormat: 'yyyy-MM-dd' })
    acc[sortValue] = [...(acc[sortValue] ?? []), row]
    return acc
  }, {})

export const sortDataByMetric = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  sort,
  metric,
  data
}: {
  sort: TableColumnSortType
  metric: DataKey
  data: DataRow[]
}): DataRow[] => {
  if (sort === false) {
    return data
  }

  const mappedData = data.reduce<Record<string, DataRow[]>>((acc, row) => {
    const sortValue = defaultToZero(row[metric] as number | null | undefined)
    acc[sortValue] = [...(acc[sortValue] ?? []), row]
    return acc
  }, {})

  return sortMappedDataNumerically(mappedData, sort)
}

export const sortDataByValues = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  sort,
  dimension,
  metrics,
  options,
  data
}: {
  sort: DimensionValuesSortType
  dimension: Dimension<'choice', DataKey, DataRow>
  metrics: DataKey[]
  options: ChartChoiceOptions<string>
  data: DataRow[]
}): DataRow[] => {
  switch (sort) {
    case 'asc':
    case 'desc': {
      const mappedData =
        dimension.type === 'date'
          ? mapAggregatedDataForDate(data, dimension.id)
          : mapAggregatedData(data, dimension.id, options)

      return sortMappedDataAlphabetically<DataKey, DataRow>(mappedData, sort)
    }
    case 'metricsAsc':
    case 'metricsDesc': {
      const mappedData = data.reduce<Record<string, DataRow[]>>((acc, row) => {
        const sortValue = metrics.reduce<number>(
          (acc, metric) => acc + defaultToZero(row[metric] as number | null | undefined),
          0
        )
        acc[sortValue] = [...(acc[sortValue] ?? []), row]
        return acc
      }, {})

      return sortMappedDataNumerically(mappedData, sort)
    }
    default:
      assertNever(sort)
      return data
  }
}

const sortDataByDimensionValues = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  cardId,
  choice,
  dimension,
  metrics,
  data
}: {
  cardId: DataKey
  choice: ChartDimensionValuesChoice<DataKey, DataRow> | ChartVariantValuesChoice<DataKey, DataRow>
  dimension: Dimension<'choice', DataKey, DataRow>
  metrics: DataKey[]
  data: DataRow[]
}): DataRow[] => {
  const { id, sort, change } = choice

  if (sort == null) {
    return data
  }

  if (typeof sort === 'function') {
    return sort(cardId, id, dimension.id, metrics, data)
  }

  return sortDataByValues<DataKey, DataRow>({
    sort,
    dimension,
    metrics,
    options: change.options,
    data
  })
}

const sortDataByDimensionValuesOf = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  cardId,
  choice,
  dimension,
  metrics,
  data
}: {
  cardId: DataKey
  choice: ChartDimensionValuesOfChoice<DataKey, DataRow>
  dimension: Dimension<'choice', DataKey, DataRow> | undefined
  metrics: DataKey[]
  data: DataRow[]
}): DataRow[] => {
  const { id, sort, change } = choice

  if (sort == null || dimension == null) {
    return data
  }

  if (typeof sort === 'function') {
    return sort(cardId, id, dimension.id, metrics, data)
  }

  return sortDataByValues<DataKey, DataRow>({
    sort,
    dimension,
    metrics,
    options: change.options[dimension.id],
    data
  })
}

export const sortDataIfFirstAxisChoiceValuesOrValuesOf = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  Data extends DataRow[] | undefined
>({
  cardId,
  chart,
  metrics,
  axisDimension,
  axisChoice,
  data
}: {
  cardId: DataKey
  chart: ChartCardChartDefinition<DataKey, DataRow>
  metrics: DataKey[]
  axisDimension: Dimension<'choice', DataKey, DataRow> | undefined
  axisChoice: ChartDimensionAxisChoice<DataKey, DataRow> | undefined
  data: Data
}): Data => {
  if (axisDimension == null || data == null) {
    return data
  }

  const { type: chartType, firstAxis, thirdAxis, dimensionAxis } = chart
  const choice: ChartDimensionAxisChoice<DataKey, DataRow> = axisChoice ?? {
    axis: dimensionAxis,
    chartType,
    id: dimensionAxis === 'third' ? thirdAxis : firstAxis,
    type: 'values',
    dimension: axisDimension.id,
    sort: axisDimension.type === 'date' ? 'asc' : undefined,
    change: {
      change: 'static',
      options: []
    }
  }

  if (choice.type === 'valuesOf') {
    return sortDataByDimensionValuesOf({ cardId, choice, dimension: axisDimension, metrics, data }) as Data
  }

  return sortDataByDimensionValues({ cardId, choice, dimension: axisDimension, metrics, data }) as Data
}

export function filterDataIfFirstAxisChoiceValuesOrValuesOf<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>({
  cardId,
  chart,
  metrics,
  axisDimension,
  axisChoice,
  data
}: {
  cardId: DataKey
  chart: ChartCardChartDefinition<DataKey, DataRow>
  metrics: DataKey[]
  axisDimension: Dimension<'choice', DataKey, DataRow> | undefined
  axisChoice: ChartDimensionAxisChoice<DataKey, DataRow> | undefined
  data: undefined
}): undefined
export function filterDataIfFirstAxisChoiceValuesOrValuesOf<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>({
  cardId,
  chart,
  metrics,
  axisDimension,
  axisChoice,
  data
}: {
  cardId: DataKey
  chart: ChartCardChartDefinition<DataKey, DataRow>
  metrics: DataKey[]
  axisDimension: Dimension<'choice', DataKey, DataRow> | undefined
  axisChoice: ChartDimensionAxisChoice<DataKey, DataRow> | undefined
  data: DataRow[]
}): DataRow[]
export function filterDataIfFirstAxisChoiceValuesOrValuesOf<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>({
  cardId,
  chart,
  metrics,
  axisDimension,
  axisChoice,
  data
}: {
  cardId: DataKey
  chart: ChartCardChartDefinition<DataKey, DataRow>
  metrics: DataKey[]
  axisDimension: Dimension<'choice', DataKey, DataRow> | undefined
  axisChoice: ChartDimensionAxisChoice<DataKey, DataRow> | undefined
  data: DataRow[] | undefined
}): DataRow[] | undefined
export function filterDataIfFirstAxisChoiceValuesOrValuesOf<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>({
  cardId,
  chart,
  metrics,
  axisDimension,
  axisChoice,
  data
}: {
  cardId: DataKey
  chart: ChartCardChartDefinition<DataKey, DataRow>
  metrics: DataKey[]
  axisDimension: Dimension<'choice', DataKey, DataRow> | undefined
  axisChoice: ChartDimensionAxisChoice<DataKey, DataRow> | undefined
  data: DataRow[] | undefined
}): DataRow[] | undefined {
  if (axisChoice == null || data == null) {
    return data
  }

  const { id, filter } = axisChoice
  if (filter == null) {
    return data
  }

  if (typeof filter === 'function' && axisDimension != null) {
    return filter(cardId, id, axisDimension.id, metrics, data)
  }
  if (filter === 'valuedMetrics') {
    return data.filter((row) => metrics.some((metric) => row[metric] !== null && row[metric] !== 0))
  }

  return data
}

export const filterDimensionValues = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  dimensionId,
  options,
  metrics,
  filter,
  data
}: {
  dimensionId: DataKey
  options: string[]
  metrics: DataKey[] | undefined
  filter: MetricFilterType | ChartFilterValues<DataKey, DataRow> | undefined
  data: DataRow[]
}): string[] => {
  if (filter == null || metrics == null) {
    return options
  }

  if (filter === 'valuedMetrics') {
    return options.filter((option) =>
      metrics?.some((metric) =>
        data.some((row) => {
          const rowValue =
            typeof row[dimensionId] === 'string' || row[dimensionId] == null || row[dimensionId] === ''
              ? toFilterableStringDimensionValue(row[dimensionId])
              : row[dimensionId]
          return rowValue === option && row[metric] !== null && row[metric] !== 0
        })
      )
    )
  }

  return options
}

export const orderTransposedMetricsLists = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  transposedMetricsLists: Map<DataKey, Set<DataKey>>,
  transposeMetrics: Array<TransposeMetric<DataKey>>,
  data: DataRow[]
): Map<DataKey, Set<DataKey>> => {
  const orderedTransposedMetricsLists = new Map(transposedMetricsLists)

  for (const { choiceId, metric, sort } of transposeMetrics) {
    const metrics = transposedMetricsLists.get(choiceId) as Set<DataKey>

    if (metrics == null) {
      continue
    }

    if (metrics.size > 1 && (sort === 'metricsAsc' || sort === 'metricsDesc')) {
      const metricsArray = Array.from(metrics)

      const totalsMap = metricsArray.reduce<Partial<Record<DataKey, number>>>((acc, metric) => {
        acc[metric] = aggregateMetric(data, { id: metric, type: 'number', aggregation: 'sum', missingValue: 0 }) ?? 0
        return acc
      }, {}) as Record<DataKey, number>

      const sortFunction =
        sort === 'metricsAsc'
          ? (a: DataKey, b: DataKey): number => totalsMap[a] - totalsMap[b]
          : (a: DataKey, b: DataKey): number => totalsMap[b] - totalsMap[a]

      const orderedMetrics = new Set(arrayToSorted(metricsArray, sortFunction))

      orderedTransposedMetricsLists.set(metric, orderedMetrics)
      orderedTransposedMetricsLists.set(choiceId, orderedMetrics)
    }
  }

  return orderedTransposedMetricsLists
}

const getDateFormatted = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  row: DataRow,
  dateKey: DataKey
): string | null => {
  const date = row[dateKey] as string | null | undefined
  return date == null ? null : formatDateToString({ date, targetFormat: 'yyyy-MM-dd' })
}

export function fillMissingDateRangeData<DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  axisDimension,
  data,
  dateRange
}: {
  axisDimension: Dimension<'choice', DataKey, DataRow> | undefined
  data: undefined
  dateRange: DateRangeChoice | undefined
}): undefined
export function fillMissingDateRangeData<DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  axisDimension,
  data,
  dateRange
}: {
  axisDimension: Dimension<'choice', DataKey, DataRow> | undefined
  data: DataRow[]
  dateRange: DateRangeChoice | undefined
}): DataRow[]
export function fillMissingDateRangeData<DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  axisDimension,
  data,
  dateRange
}: {
  axisDimension: Dimension<'choice', DataKey, DataRow> | undefined
  data: DataRow[] | undefined
  dateRange: DateRangeChoice | undefined
}): DataRow[] | undefined
export function fillMissingDateRangeData<DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  axisDimension,
  data,
  dateRange
}: {
  axisDimension: Dimension<'choice', DataKey, DataRow> | undefined
  data: DataRow[] | undefined
  dateRange: DateRangeChoice | undefined
}): DataRow[] | undefined {
  if (dateRange == null || data == null) {
    return data
  }

  if (axisDimension?.type !== 'date') {
    return data
  }

  const { startDate, endDate } = dateRange
  const daysInRange = differenceInDays(endDate, startDate)
  const rangeSet = new Set<string>()
  for (let day = 0; day <= daysInRange; day++) {
    rangeSet.add(formatDateToString({ date: addDays(startDate, day), targetFormat: 'yyyy-MM-dd' }))
  }
  const dateKey = axisDimension.id
  const dataDates = data.map((row) => getDateFormatted(row, dateKey))
  const missingDates = setDifference(rangeSet, new Set(dataDates.filter((date) => date != null)))

  if (missingDates.size === 0) {
    return data
  }

  for (const newDate of missingDates) {
    // Placeholder row: only date key set; assertion needed because computed key widens to { [x: string]: string }
    const placeholderRow = { [dateKey]: newDate }
    data.push(placeholderRow as DataRow)
  }

  return data
}

export const dataIsReady = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  data: ChartCardDataProps<DataKey, DataRow>
): data is ChartCardDataPropsReady<DataKey, DataRow> =>
  isChartCardClientDataProps(data) && data.data != null && !data.isLoading && !data.isError

export const inconsistentProcessedVsOriginalData = (
  processedData: any[] | undefined,
  originalData: any[] | undefined
): boolean =>
  (processedData == null) !== (originalData == null) || (processedData?.length === 0) !== (originalData?.length === 0)
